import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Alert, AlertDescription } from './ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  CheckSquare, 
  Bell, 
  DollarSign, 
  Gift, 
  Plus,
  AlertCircle,
  Loader2,
  Shield,
  MessageSquare,
  Heart
} from 'lucide-react';
import { api, DashboardData, DashboardCommunication, UserProfile } from '../lib/api';
import { toast } from 'sonner';
import LogRocket from 'logrocket';
import { PayPalFunds } from './PayPalFunds';

interface DashboardProps {
  onNavigate: (screen: string, data?: { giftGroupId?: string; memberId?: string; tab?: string; view?: string; suggestionId?: string; openComment?: string }) => void;
  initialTab?: string;
  giftGroupId?: string | null;
  recommendedAmount?: number | null;
}

export function Dashboard({ onNavigate, initialTab = 'todo', giftGroupId, recommendedAmount }: DashboardProps) {
  console.log('📊 Dashboard render - initialTab:', initialTab);
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedComm, setSelectedComm] = useState<{
    date: string;
    type: string;
    from: string;
    message: string;
    replyTo?: string;
  } | null>(null);
  const [replyContext, setReplyContext] = useState<{
    mode: 'admin-feedback-reply' | 'support-reply';
    commId?: string;
    toUserId?: string;
    toName: string;
    giftGroupId?: string | null;
  } | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);

  // Update active tab when initialTab changes
  useEffect(() => {
    console.log('📊 Dashboard useEffect - initialTab changed to:', initialTab);
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [dashboard, profile] = await Promise.all([
        api.getDashboard(),
        api.getProfile()
      ]);
      
      setDashboardData(dashboard);
      setUserProfile(profile);
      
      // Identify user in LogRocket for session tracking
      if (profile) {
        try {
          const identifyData: Record<string, string | number | boolean> = {
            name: profile.name || 'Unknown',
          };
          if (profile.email) identifyData.email = profile.email;
          if (profile.phone) identifyData.phone = profile.phone;
          
          console.log('[LogRocket] Identifying user:', profile.id, identifyData);
          LogRocket.identify(profile.id.toString(), identifyData);
          console.log('[LogRocket] User identified successfully');
        } catch (error) {
          console.error('[LogRocket] Failed to identify user:', error);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard';
      setError(errorMessage);
      toast.error('Failed to load dashboard', {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    // Handle ISO timestamps, SQLite datetime, and YYYY-MM-DD dates
    if (!dateString) return 'N/A';
    
    let date: Date;
    
    // If it's an ISO timestamp (contains 'T'), parse it normally
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else if (dateString.includes(' ')) {
      // SQLite datetime format: "YYYY-MM-DD HH:MM:SS"
      // Replace space with 'T' to make it ISO-compatible
      date = new Date(dateString.replace(' ', 'T'));
    } else {
      // Parse YYYY-MM-DD as local date, not UTC (prevents off-by-one day issues)
      const [year, month, day] = dateString.split('-').map(Number);
      date = new Date(year, month - 1, day);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    // Use browser's locale for proper localization
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleFundsAdded = () => {
    // Refresh dashboard data after successful payment
    loadDashboardData();
    
    // Switch to TODO tab to see gift group tasks
    setActiveTab('todo');
    
    // If this was for a specific gift group, provide feedback
    if (giftGroupId) {
      toast.success('Ready to view your gift groups!');
    }
  };

  const openSupportReplyDialog = (params: {
    mode: 'admin-feedback-reply' | 'support-reply';
    toName: string;
    commId?: string;
    toUserId?: string;
    giftGroupId?: string | null;
  }) => {
    setReplyContext(params);
    setReplyMessage('');
  };

  const handleSubmitSupportReply = async () => {
    if (!replyContext) return;
    if (!replyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    try {
      setIsSendingReply(true);
      if (replyContext.mode === 'admin-feedback-reply' && replyContext.toUserId && replyContext.commId) {
        await api.replyToFeedback({
          toUserId: replyContext.toUserId,
          feedback: replyMessage.trim(),
          originalFeedbackId: replyContext.commId,
        });
      } else {
        await api.submitFeedback({
          feedback: replyMessage.trim(),
          currentPage: 'dashboard',
          currentView: 'communications',
          giftGroupId: replyContext.giftGroupId || undefined,
          originalFeedbackId: replyContext.commId,
        });
      }
      toast.success('Reply sent');
      setReplyContext(null);
      setReplyMessage('');
      await loadDashboardData();
    } catch (err) {
      toast.error('Failed to send reply', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setIsSendingReply(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center">
        <Card className="border border-primary/20 shadow-lg p-8">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <p className="text-muted-foreground">Loading your dashboard...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !dashboardData || !userProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="border border-primary/20 shadow-lg p-8 max-w-md w-full">
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error || 'Failed to load dashboard data'}
            </AlertDescription>
          </Alert>
          <Button onClick={loadDashboardData} className="w-full">
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20">
      {/* Dashboard Header */}
      <div className="bg-background border-b border-primary/20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-primary">Gifter Dashboard</h1>
            </div>
            <div className="flex gap-2 flex-wrap w-full sm:w-auto">
              {userProfile.adminLevel && (
                <Button
                  onClick={() => onNavigate('admin')}
                  variant="outline"
                  className="border-orange-500 text-orange-600 hover:bg-orange-50 flex-1 sm:flex-none"
                >
                  <Shield className="w-4 h-4 sm:mr-2" />
                  <span className="hidden xs:inline">Admin</span>
                  <span className="xs:hidden">Admin</span>
                </Button>
              )}
              <Button
                onClick={() => onNavigate('create-gift-group')}
                className="bg-primary hover:bg-primary/90 flex-1 sm:flex-none"
              >
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden xs:inline">Create Gift Group</span>
                <span className="xs:hidden">Create</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* TODO: Search functionality will be implemented in the Groups view */}
          <TabsList className="grid w-full grid-cols-4 h-auto bg-background/80 border border-primary/20 overflow-x-auto">
            <TabsTrigger value="todo" className="flex-col gap-1 py-2 sm:py-3 px-2">
              <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-[10px] sm:text-xs">To-Do</span>
              {dashboardData.todos.length > 0 && (
                <span className="text-[10px] sm:text-xs font-medium text-primary">
                  {dashboardData.todos.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="communications" className="flex-col gap-1 py-2 sm:py-3 px-2">
              <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-[10px] sm:text-xs hidden xs:inline">Comments</span>
              <span className="text-[10px] sm:text-xs xs:hidden">💬</span>
              {dashboardData.communications.length > 0 && (
                <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-green-100 border border-green-500 text-green-700 text-[10px] sm:text-xs font-medium">
                  {dashboardData.communications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="finances" className="flex-col gap-1 py-2 sm:py-3 px-2">
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-[10px] sm:text-xs">Money</span>
            </TabsTrigger>
            <TabsTrigger value="gift-groups" className="flex-col gap-1 py-2 sm:py-3 px-2">
              <Gift className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-[10px] sm:text-xs">Groups</span>
              {dashboardData.giftGroups.length > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] sm:text-xs font-medium text-primary border-2 border-primary rounded-full">
                  {dashboardData.giftGroups.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* To-Do Tab */}
          <TabsContent value="todo" className="space-y-4">
            <Card className="border border-primary/20 shadow-lg">
              <div className="p-4 border-b border-primary/20">
                <h2 className="text-primary">Outstanding Tasks To-Do</h2>
              </div>
              
              {dashboardData.todos.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No outstanding tasks! 🎉</p>
                  <p className="text-sm mt-1">You're all caught up.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Action</TableHead>
                        <TableHead className="whitespace-nowrap">Gift Date</TableHead>
                        <TableHead className="whitespace-nowrap">Gift Group Type</TableHead>
                        <TableHead className="whitespace-nowrap">Gift Group</TableHead>
                        <TableHead className="whitespace-nowrap">Giftee(s)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardData.todos.map((todo) => (
                        <TableRow key={todo.member_id}>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="default"
                              onClick={() => {
                                if (todo.type === 'accept-invitation') {
                                  onNavigate('gift-group-acceptance', { memberId: todo.member_id });
                                } else if (todo.type === 'explore-ai-gifts') {
                                  onNavigate('gift-group-overview', { 
                                    giftGroupId: todo.gift_group_id,
                                    view: 'ai-suggestions'
                                  });
                                } else if (todo.type === 'add-manual-gifts') {
                                  onNavigate('gift-group-overview', { 
                                    giftGroupId: todo.gift_group_id,
                                    view: 'manual-suggestions'
                                  });
                                } else {
                                  // rank-gifts and others default to ranking view
                                  onNavigate('gift-group-overview', { giftGroupId: todo.gift_group_id });
                                }
                              }}
                              className="whitespace-nowrap"
                            >
                              {todo.type === 'accept-invitation' && 'Accept Invitation'}
                              {todo.type === 'explore-ai-gifts' && 'Explore AI Gifts'}
                              {todo.type === 'add-manual-gifts' && 'Add Manual Gifts'}
                              {todo.type === 'rank-gifts' && 'Rank Gifts'}
                            </Button>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{formatDate(todo.due_date)}</TableCell>
                          <TableCell className="whitespace-nowrap">{todo.type_name || 'N/A'}</TableCell>
                          <TableCell className="max-w-[150px] md:max-w-none truncate">{todo.gift_group_name}</TableCell>
                          <TableCell className="max-w-[150px] md:max-w-none">
                            {todo.giftees ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="truncate block cursor-help">{todo.giftees}</span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{todo.giftees}</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span>N/A</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications" className="space-y-4">
            <Card className="border border-primary/20 shadow-lg">
              <div className="p-4 border-b border-primary/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h2 className="text-primary">Messages & Notifications</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Gift comments, @mentions, feedback, and welcome messages
                  </p>
                </div>
                {dashboardData.giftGroups.filter(g => g.role !== 'giftee').length > 0 && (
                  <Button
                    size="sm"
                    className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
                    onClick={() => setActiveTab('gift-groups')}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Comment
                  </Button>
                )}
              </div>
              
              {dashboardData.communications.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No new messages</p>
                  <p className="text-sm mt-1">Comments, mentions, and feedback will appear here!</p>
                </div>
              ) : (
                <>
                  {/* Mobile Card Layout */}
                  <div className="md:hidden p-4 space-y-3">
                    {dashboardData.communications.map((comm) => {
                      // Extended communication properties from combined query
                      const extComm = comm as DashboardCommunication & {
                        comm_type?: string;
                        gift_name?: string;
                        commenter_name?: string;
                        commenter_email?: string;
                        commenter_phone?: string;
                        commenter_id?: string;
                        is_admin_reply?: number | string | boolean;
                        reply_to_feedback_id?: string;
                        reply_to_message?: string;
                        suggestion_id?: string;
                      };
                      const isReply = !!extComm.reply_to_feedback_id;
                      const isAdminReply = extComm.is_admin_reply === true || extComm.is_admin_reply === 1 || extComm.is_admin_reply === '1';
                      const isFeedback = extComm.comm_type === 'feedback';
                      const isWelcome = extComm.comm_type === 'welcome';
                      const isGiftComment = !isFeedback && !isWelcome && !!comm.gift_group_id;
                      const canAdminReplyToFeedback = !!(
                        isFeedback &&
                        userProfile.adminLevel &&
                        extComm.commenter_id &&
                        extComm.commenter_id !== userProfile.id
                      );
                      const commenterName = extComm.commenter_name || extComm.commenter_email || extComm.commenter_phone || 'Unknown';
                      const commTypeLabel = (isReply || isAdminReply)
                        ? 'Reply'
                        : isFeedback
                          ? 'Feedback'
                          : isWelcome
                            ? 'Welcome'
                            : extComm.gift_name || 'Gift Comment';
                      const replyToText = extComm.reply_to_message
                        ? extComm.reply_to_message
                        : extComm.reply_to_feedback_id
                          ? `Message ${extComm.reply_to_feedback_id}`
                          : undefined;
                      
                      return (
                        <Card key={comm.id} className="p-4 border border-primary/10">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-sm text-muted-foreground">{formatDate(comm.created_at)}</span>
                            {(isReply || isAdminReply) ? (
                              <Badge variant="secondary" className="flex items-center gap-1 shrink-0">
                                <MessageSquare className="w-3 h-3" />
                                Reply
                              </Badge>
                            ) : isFeedback ? (
                              <Badge variant="secondary" className="flex items-center gap-1 shrink-0">
                                <MessageSquare className="w-3 h-3" />
                                Feedback
                              </Badge>
                            ) : isWelcome ? (
                              <Badge variant="default" className="flex items-center gap-1 shrink-0 bg-primary">
                                <Heart className="w-3 h-3" />
                                Welcome
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm shrink-0">
                                {extComm.gift_name || 'Gift Comment'}
                              </span>
                            )}
                          </div>
                          {replyToText && (
                            <p className="text-xs text-muted-foreground mb-1 line-clamp-1 break-all">
                              Reply to: {replyToText}
                            </p>
                          )}
                          <p className="text-sm mb-2 line-clamp-3 break-all">{comm.message}</p>
                          <div className="flex justify-between items-center text-sm text-muted-foreground">
                            <span className="truncate">From: {commenterName}</span>
                            <div className="flex items-center gap-2 shrink-0 ml-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-primary/30 hover:bg-primary/5"
                                onClick={() =>
                                  setSelectedComm({
                                    date: formatDate(comm.created_at),
                                    type: commTypeLabel,
                                    from: commenterName,
                                    message: comm.message,
                                    replyTo: replyToText,
                                  })
                                }
                              >
                                Read
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="border-primary/30 hover:bg-primary/5"
                                onClick={() => {
                                  if (isGiftComment) {
                                    // Navigate to gift group with suggestion_id and openComment flag
                                    onNavigate('gift-group-overview', {
                                      giftGroupId: comm.gift_group_id,
                                      suggestionId: extComm.suggestion_id,
                                      openComment: 'true'
                                    });
                                  } else if (canAdminReplyToFeedback) {
                                    openSupportReplyDialog({
                                      mode: 'admin-feedback-reply',
                                      commId: comm.id,
                                      toUserId: extComm.commenter_id!,
                                      toName: commenterName,
                                      giftGroupId: comm.gift_group_id,
                                    });
                                  } else {
                                    openSupportReplyDialog({
                                      mode: 'support-reply',
                                      commId: comm.id,
                                      toName: commenterName,
                                      giftGroupId: comm.gift_group_id,
                                    });
                                  }
                                }}
                              >
                                Reply
                              </Button>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>

                  <Dialog open={!!selectedComm} onOpenChange={(open) => !open && setSelectedComm(null)}>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Full Message</DialogTitle>
                      </DialogHeader>
                      {selectedComm && (
                        <div className="space-y-3 text-sm">
                          <div className="text-muted-foreground">
                            {selectedComm.date} · {selectedComm.type}
                          </div>
                          {selectedComm.replyTo && (
                            <div className="text-muted-foreground text-xs break-all">
                              Reply to: {selectedComm.replyTo}
                            </div>
                          )}
                          <div className="whitespace-pre-wrap break-all rounded-md border border-primary/15 p-3 bg-muted/20">
                            {selectedComm.message}
                          </div>
                          <div className="text-muted-foreground">From: {selectedComm.from}</div>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>

                  <Dialog open={!!replyContext} onOpenChange={(open) => !open && setReplyContext(null)}>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Reply</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Replying to: {replyContext?.toName}
                        </p>
                        <Textarea
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          placeholder="Write your reply..."
                          className="min-h-[120px] resize-none"
                          maxLength={5000}
                          disabled={isSendingReply}
                        />
                        <div className="text-xs text-muted-foreground text-right">
                          {replyMessage.length}/5000 characters
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            onClick={() => setReplyContext(null)}
                            disabled={isSendingReply}
                          >
                            Cancel
                          </Button>
                          <Button
                            className="bg-primary hover:bg-primary/90"
                            onClick={handleSubmitSupportReply}
                            disabled={isSendingReply || !replyMessage.trim()}
                          >
                            {isSendingReply ? 'Sending...' : 'Send Reply'}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Desktop Table Layout */}
                  <div className="hidden md:block w-full">
                    <Table className="table-fixed w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap w-[100px] align-top">Date</TableHead>
                          <TableHead className="whitespace-nowrap w-[120px] align-top">Type</TableHead>
                          <TableHead className="whitespace-nowrap align-top">Message</TableHead>
                          <TableHead className="whitespace-nowrap w-[120px] align-top">From</TableHead>
                          <TableHead className="whitespace-nowrap w-[170px] align-top">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dashboardData.communications.map((comm) => {
                          // Extended communication properties from combined query
                          const extComm = comm as DashboardCommunication & {
                            comm_type?: string;
                            gift_name?: string;
                            commenter_name?: string;
                            commenter_email?: string;
                            commenter_phone?: string;
                            commenter_id?: string;
                            is_admin_reply?: number | string | boolean;
                            reply_to_feedback_id?: string;
                            reply_to_message?: string;
                            suggestion_id?: string;
                          };
                          const isReply = !!extComm.reply_to_feedback_id;
                          const isAdminReply = extComm.is_admin_reply === true || extComm.is_admin_reply === 1 || extComm.is_admin_reply === '1';
                          const isFeedback = extComm.comm_type === 'feedback';
                          const isWelcome = extComm.comm_type === 'welcome';
                          const isGiftComment = !isFeedback && !isWelcome && !!comm.gift_group_id;
                          const canAdminReplyToFeedback = !!(
                            isFeedback &&
                            userProfile.adminLevel &&
                            extComm.commenter_id &&
                            extComm.commenter_id !== userProfile.id
                          );
                          const commenterName = extComm.commenter_name || extComm.commenter_email || extComm.commenter_phone || 'Unknown';
                          const commTypeLabel = (isReply || isAdminReply)
                            ? 'Reply'
                            : isFeedback
                              ? 'Feedback'
                              : isWelcome
                                ? 'Welcome'
                                : extComm.gift_name || 'Gift Comment';
                          const replyToText = extComm.reply_to_message
                            ? extComm.reply_to_message
                            : extComm.reply_to_feedback_id
                              ? `Message ${extComm.reply_to_feedback_id}`
                              : undefined;
                          
                          return (
                            <TableRow key={comm.id}>
                              <TableCell className="whitespace-nowrap align-top">{formatDate(comm.created_at)}</TableCell>
                              <TableCell className="whitespace-nowrap align-top">
                                {(isReply || isAdminReply) ? (
                                  <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                                    <MessageSquare className="w-3 h-3" />
                                    Reply
                                  </Badge>
                                ) : isFeedback ? (
                                  <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                                    <MessageSquare className="w-3 h-3" />
                                    Feedback
                                  </Badge>
                                ) : isWelcome ? (
                                  <Badge variant="default" className="flex items-center gap-1 w-fit bg-primary">
                                    <Heart className="w-3 h-3" />
                                    Welcome
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground text-sm">
                                    {extComm.gift_name || 'Gift Comment'}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="align-top">
                                <div className="whitespace-normal break-words">
                                  {replyToText && (
                                    <p className="text-xs text-muted-foreground mb-1 break-all">
                                      Reply to: {replyToText}
                                    </p>
                                  )}
                                  {comm.message}
                                </div>
                              </TableCell>
                              <TableCell className="whitespace-nowrap truncate align-top">{commenterName}</TableCell>
                              <TableCell className="align-top">
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-primary/30 hover:bg-primary/5 whitespace-nowrap"
                                    onClick={() =>
                                      setSelectedComm({
                                        date: formatDate(comm.created_at),
                                        type: commTypeLabel,
                                        from: commenterName,
                                        message: comm.message,
                                        replyTo: replyToText,
                                      })
                                    }
                                  >
                                    Read
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="border-primary/30 hover:bg-primary/5 whitespace-nowrap"
                                    onClick={() => {
                                      if (isGiftComment) {
                                        // Navigate to gift group with suggestion_id and openComment flag
                                        onNavigate('gift-group-overview', {
                                          giftGroupId: comm.gift_group_id,
                                          suggestionId: extComm.suggestion_id,
                                          openComment: 'true'
                                        });
                                      } else if (canAdminReplyToFeedback) {
                                        openSupportReplyDialog({
                                          mode: 'admin-feedback-reply',
                                          commId: comm.id,
                                          toUserId: extComm.commenter_id!,
                                          toName: commenterName,
                                          giftGroupId: comm.gift_group_id,
                                        });
                                      } else {
                                        openSupportReplyDialog({
                                          mode: 'support-reply',
                                          commId: comm.id,
                                          toName: commenterName,
                                          giftGroupId: comm.gift_group_id,
                                        });
                                      }
                                    }}
                                  >
                                    Reply
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </Card>
          </TabsContent>

          {/* Finances Tab */}
          <TabsContent value="finances" className="space-y-4">
            {/* Balance Summary Cards */}
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="border border-primary/20 shadow-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">Current Credit Balance</div>
                  <div className="text-primary">{formatCurrency(dashboardData.finances.creditBalance)}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Available for contributions
                </div>
              </Card>
              
              <Card className="border border-primary/20 shadow-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">Allocated to Gift Groups</div>
                  <div className="text-primary">{formatCurrency(dashboardData.finances.allocated)}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  {dashboardData.giftGroups.filter(g => g.member_status === 'contributed').length} active groups
                </div>
              </Card>
              
              <Card className="border border-primary/20 shadow-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">Upcoming Budget</div>
                  <div className="text-primary">{formatCurrency(dashboardData.finances.upcoming90Days)}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-2">Next 90 days</div>
              </Card>
            </div>

            {/* PayPal Add Funds */}
            <PayPalFunds 
              giftGroupId={giftGroupId}
              recommendedAmount={recommendedAmount}
              onSuccess={handleFundsAdded}
            />

            {/* Test Funds Button (Development Only) */}
            {import.meta.env.DEV && (
              <Card className="border border-primary/20 shadow-lg p-6">
                <div className="mb-4">
                  <h3 className="text-primary mb-2">Development Testing</h3>
                  <p className="text-sm text-muted-foreground">
                    Add test funds without payment (development only)
                  </p>
                </div>
                <Button 
                  className="w-full bg-primary hover:bg-primary/90" 
                  size="sm"
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      const result = await api.addTestFunds(100);
                      toast.success('Test funds added!', {
                        description: `Added $100. New balance: ${formatCurrency(result.newBalance)}`,
                      });
                      loadDashboardData();
                    } catch (err) {
                      toast.error('Failed to add test funds');
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add $100 (Test)'
                  )}
                </Button>
              </Card>
            )}

            <Card className="border border-primary/20 shadow-lg">
              <div className="p-4 border-b border-primary/20">
                <h2 className="text-primary">Transaction Ledger</h2>
              </div>
              
              {dashboardData.finances.recentTransactions.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No transactions yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Date</TableHead>
                        <TableHead className="whitespace-nowrap">Description</TableHead>
                        <TableHead className="whitespace-nowrap">Reference</TableHead>
                        <TableHead className="whitespace-nowrap">Amount</TableHead>
                        <TableHead className="whitespace-nowrap">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardData.finances.recentTransactions.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell className="whitespace-nowrap">{formatDate(tx.created_at)}</TableCell>
                          <TableCell className="max-w-[150px] md:max-w-none truncate">{tx.description}</TableCell>
                          <TableCell className="text-xs text-muted-foreground font-mono max-w-[120px] truncate" title={tx.payment_provider_id || ''}>
                            {tx.payment_provider_id ? (
                              <span>
                                {tx.payment_provider && <span className="text-primary">{tx.payment_provider}: </span>}
                                {tx.payment_provider_id}
                              </span>
                            ) : (
                              <span className="text-muted-foreground/50">—</span>
                            )}
                          </TableCell>
                          <TableCell className={`whitespace-nowrap ${tx.amount > 0 ? 'text-primary' : 'text-destructive'}`}>
                            {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{formatCurrency(tx.balance_after)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Gift Groups Tab */}
          <TabsContent value="gift-groups" className="space-y-4">
            <Card className="border border-primary/20 shadow-lg">
              <div className="p-4 border-b border-primary/20">
                <h2 className="text-primary">My Gift Groups</h2>
              </div>
              
              {dashboardData.giftGroups.filter((group) => group.role !== 'giftee').length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No gift groups yet</p>
                  <p className="text-sm mt-1">Create your first gift group to get started!</p>
                  <Button 
                    className="mt-4 bg-primary hover:bg-primary/90"
                    onClick={() => onNavigate('create-gift-group')}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Gift Group
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Action</TableHead>
                        <TableHead className="whitespace-nowrap">Gift Date</TableHead>
                        <TableHead className="whitespace-nowrap">Type</TableHead>
                        <TableHead className="whitespace-nowrap">Gift Group</TableHead>
                        <TableHead className="whitespace-nowrap">Giftee</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardData.giftGroups.filter((group) => group.role !== 'giftee').map((group) => {
                        // Determine workflow state button text and variant
                        const workflowState = group.workflow_state || 'pending_acceptance';
                        let buttonText = 'View';
                        let buttonVariant = 'outline' as 'default' | 'destructive' | 'outline';
                        
                        // NOTE: pending_contribution and pending_shipping_address steps are temporarily disabled
                        switch (workflowState) {
                          case 'pending_acceptance':
                            buttonText = 'Accept Invitation';
                            buttonVariant = 'default';
                            break;
                          case 'pending_ai_gifts':
                            buttonText = 'Explore AI Gifts';
                            buttonVariant = 'default';
                            break;
                          case 'pending_manual_gifts':
                            buttonText = 'Add Manual Gifts';
                            buttonVariant = 'default';
                            break;
                          case 'pending_ranking':
                            buttonText = 'Rank Gifts';
                            buttonVariant = 'default';
                            break;
                          case 'active':
                            buttonText = 'View Details';
                            buttonVariant = 'outline';
                            break;
                          default:
                            buttonText = 'View';
                            buttonVariant = 'outline';
                        }

                        return (
                          <TableRow key={group.id}>
                            <TableCell>
                              <Button 
                                size="sm" 
                                variant={buttonVariant}
                                onClick={() => {
                                  if (workflowState === 'pending_acceptance') {
                                    onNavigate('gift-group-acceptance', { memberId: group.id });
                                  } else if (workflowState === 'pending_ai_gifts') {
                                    onNavigate('gift-group-overview', { 
                                      giftGroupId: group.id,
                                      view: 'ai-suggestions'
                                    });
                                  } else if (workflowState === 'pending_manual_gifts') {
                                    onNavigate('gift-group-overview', { 
                                      giftGroupId: group.id,
                                      view: 'manual-suggestions'
                                    });
                                  } else {
                                    // rank-gifts and other states default to ranking view
                                    onNavigate('gift-group-overview', { giftGroupId: group.id });
                                  }
                                }}
                                className="whitespace-nowrap"
                              >
                                {buttonText}
                              </Button>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">{formatDate(group.gift_date)}</TableCell>
                            <TableCell className="whitespace-nowrap">{group.type_name}</TableCell>
                            <TableCell className="truncate max-w-[150px] md:max-w-none">{group.title}</TableCell>
                            <TableCell className="truncate max-w-[150px] md:max-w-none">{group.giftee_names || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}